import type {SelectOption} from '../../../data/models/UI/SelectOption';
import type {BrandColorRole, DocumentBrand, StudioFont} from '../types';
import {sourceBrandText as text} from './sourceBrandText';

/* What the video branding panel shows of a job's brand, worked out from what Studio found. */

export const trimmed = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';
export const pageNumber = (value: unknown) =>
  Number.isInteger(value) && (value as number) > 0;
export const readable = (value: unknown) => trimmed(value).replace(/_/g, ' ');

/** Accepts `#abc`, `abc`, `#aabbcc` or `aabbcc`; returns `#AABBCC` or null. */
export function normalizeHex(value: string): string | null {
  const raw = value.trim().replace(/^#?/, '');
  if (/^[0-9a-f]{6}$/i.test(raw)) return `#${raw.toUpperCase()}`;
  if (/^[0-9a-f]{3}$/i.test(raw))
    return `#${raw
      .split('')
      .map(c => c + c)
      .join('')
      .toUpperCase()}`;
  return null;
}

// Storyboards drafted before the wording change still carry the old worker message.
const LEGACY_WARNINGS: Record<string, string> = {
  'No reliable logo was found; use the detected document colors without a logo.':
    text.noReliableLogo,
};

// Brands detected before the short labels still carry the worker's old sentence.
export const styleLabel = (value: unknown) => {
  const style = readable(value);
  const legacy = /^Document-derived margin accents with (\S+) typography/.exec(
    style,
  );
  if (!legacy) return style;
  return legacy[1] === 'serif' ? text.classicMinimal : text.modernMinimal;
};

export const ROLES: {role: BrandColorRole; label: string}[] = [
  {role: 'primary', label: text.primary},
  {role: 'secondary', label: text.secondary},
];

export interface BrandColor {
  role: BrandColorRole;
  label: string;
  value: string;
}

/** The brand's valid colors, as #RRGGBB (or #RGB) in capitals. */
export const brandColors = (brand?: DocumentBrand | null): BrandColor[] =>
  ROLES.flatMap(({role, label}) => {
    const value = trimmed(brand?.colors?.[role]);
    return /^#(?:[a-f0-9]{3}|[a-f0-9]{6})$/i.test(value)
      ? [{role, label, value: value.toUpperCase()}]
      : [];
  });

/** Whether a brand has anything to show read-only; the summary shows nothing for it otherwise. */
export const brandHasContent = (brand?: DocumentBrand | null) =>
  !!brand &&
  (!!brand.logoFile ||
    brandColors(brand).length > 0 ||
    !!styleLabel(brand.style) ||
    !!trimmed(brand.fontFamily) ||
    !!readable(brand.cornerStyle));

// The video's house palette, used as the starting point when a role has no brand color.
export const DEFAULT_COLORS: Record<BrandColorRole, string> = {
  primary: '#14244B',
  secondary: '#1E6F6B',
};

/**
 * The detection's warnings an author should read. Where a logo can be uploaded, the upload
 * area explains a missing logo instead.
 */
export const brandWarnings = (
  brand: DocumentBrand | null | undefined,
  explainsMissingLogo: boolean,
) =>
  (Array.isArray(brand?.warnings) ? brand!.warnings : [])
    .map(trimmed)
    .filter(Boolean)
    .map(warning => LEGACY_WARNINGS[warning] || warning)
    .filter(warning => !(explainsMissingLogo && /logo/i.test(warning)));

/** The font a video uses when its brand names none Studio offers. */
export const DEFAULT_FONT = 'montserrat';

/**
 * What detection saved for a document set in a serif font, before fonts could be chosen: the
 * video matches it with a serif Studio does not list. ("sans-serif" videos use Montserrat.)
 */
export const MATCHED_SERIF = 'serif';

/** What detection saved for a document set in a sans-serif font: its video uses Montserrat. */
const DETECTED_SANS = 'sans-serif';

/**
 * What detection found of the document's font ("serif" or "sans-serif"), shown read-only
 * where Studio lists no fonts (older servers, or while the list is read or when it could not
 * be). A chosen font's id is not shown there: it shows by its name once the list names it.
 */
export function detectedFont(brand: DocumentBrand | null | undefined) {
  const font = trimmed(brand?.fontFamily);
  return font === MATCHED_SERIF || font === DETECTED_SANS ? font : '';
}

/**
 * The font the brand's video uses, as the font list's key: a listed font's id, MATCHED_SERIF,
 * or DEFAULT_FONT for a brand without one (or with one the list lacks), as the video renders.
 */
export function brandFont(
  brand: DocumentBrand | null | undefined,
  fonts: StudioFont[],
) {
  const font = trimmed(brand?.fontFamily);
  if (font === MATCHED_SERIF) return MATCHED_SERIF;
  return fonts.some(f => f.id === font) ? font : DEFAULT_FONT;
}

/**
 * The fonts to choose from, by name and category, in Studio's order. A video matching its
 * document's serif lists that first, until another font is chosen.
 */
export const fontOptions = (
  fonts: StudioFont[],
  shown: string,
): SelectOption[] => [
  ...(shown === MATCHED_SERIF
    ? [{key: MATCHED_SERIF, value: text.matchedSerif}]
    : []),
  ...fonts.map(({id, name, category}) => ({
    key: id,
    value: text.fontOption(name, text.fontCategories[category]),
  })),
];

/** How the font list names the font with this key (see `brandFont`). */
export const fontLabel = (fonts: StudioFont[], shown: string) =>
  fontOptions(fonts, shown).find(option => option.key === shown)?.value ?? '';
